/**
 * The fixed-timestep accumulator, kept free of the DOM so it can be driven
 * with synthetic frame deltas in tests.
 *
 * Simulation advances only in whole TICK_MS steps. Anything left over past
 * MAX_STEPS_PER_FRAME is discarded rather than banked: a throttled tab must
 * not come back and run a catch-up burst. Long gaps belong to the offline
 * system, which is the only thing allowed to reason about wall-clock time.
 */

import { MAX_STEPS_PER_FRAME, TICK_DT, TICK_MS } from '../data/balance.ts';

export interface Accumulator {
  ms: number;
}

export function createAccumulator(): Accumulator {
  return { ms: 0 };
}

/**
 * Feeds one frame's elapsed milliseconds in and runs whatever whole steps
 * that buys. Returns the number of steps actually run.
 */
export function advance(
  acc: Accumulator,
  elapsedMs: number,
  step: (dt: number) => void,
): number {
  // A negative or non-finite delta can only come from a clock oddity.
  if (Number.isFinite(elapsedMs) && elapsedMs > 0) acc.ms += elapsedMs;

  let steps = 0;
  while (acc.ms >= TICK_MS && steps < MAX_STEPS_PER_FRAME) {
    step(TICK_DT);
    acc.ms -= TICK_MS;
    steps += 1;
  }

  // Drop the backlog but keep the sub-step remainder, so the phase of the
  // simulation relative to real time does not drift after a stall.
  if (acc.ms >= TICK_MS) acc.ms %= TICK_MS;

  return steps;
}
